import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Box,
  Text,
} from "@chakra-ui/react";
import bg from "../images/announcement_bg.png";
import closeButton from "../images/close_button.png";
import line from "../images/announce_line.png";
import { getAnnouncementList } from "../../../api/getApi";
import { languageMapping } from "../../../i18n/config";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import AnnouncementContent from "./AnnouncementContent";
import MaintenanceContent from "./MaintenanceContent";

// 公告類型
const ANNOUNCE_TYPE = {
  ANNOUNCE: "announce",
  MAINTENANCE: "maintenance",
};

/**
 * @description 公告彈窗
 * @returns JSX.Element
 */
const AnnouncementModal = () => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const openModalParams = searchParams.get("announcement_open");
  const [activeTab, setActiveTab] = useState(ANNOUNCE_TYPE.ANNOUNCE);
  const [announcementList, setAnnouncementList] = useState([]);

  // 關閉彈窗
  const onClose = () => setModalOpen(false);

  useEffect(() => {
    getAnnouncementList()
      .then((response) => {
        if (!!response?.data) {
          const processedArrayData = response.data.map((item) => ({
            ...item,
            currentLang:
              item.news_language.find(
                (langItem) => langItem === languageMapping(i18next.language),
              ) || item.news_language?.[0],
          }));
          setAnnouncementList(processedArrayData);
        }
      })
      .catch((error) => {
        console.error("Error fetching announcement list:", error);
      });
  }, []);

  // 當有公告時自動打開彈窗
  useEffect(() => {
    if (announcementList.length > 0) {
      setActiveTab(ANNOUNCE_TYPE.ANNOUNCE); // 有公告時預設顯示公告頁籤
      setModalOpen(true);
    } else {
      setActiveTab(ANNOUNCE_TYPE.MAINTENANCE); // 無公告時預設顯示維護頁籤
    }
  }, [announcementList]);

  // 根據 URL 參數決定是否打開彈窗
  useEffect(() => {
    if (openModalParams === "true") {
      setModalOpen(true);

      const currentParams = new URLSearchParams(location.search);
      currentParams.delete("announcement_open");

      setSearchParams(currentParams);
    }
  }, [openModalParams, location, setSearchParams]);

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={modalOpen}
      onClose={onClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        height={{
          base: "550px",
          md: "600px",
        }}
        pb={12}
        mx={4}
        sx={{
          bgImage: `url(${bg})`,
          bgSize: "100% 100%",
          background: "none",
          position: "relative",
        }}
      >
        <Box
          onClick={onClose}
          sx={{
            height: "20px",
            width: "20px",
            cursor: "pointer",
            position: "absolute",
          }}
          right={6}
          top={6}
        >
          <Image src={closeButton} alt="Close" />
        </Box>
        <ModalHeader
          pt={5}
          pb={2}
          px={{ base: "8" }}
          textAlign={"center"}
          display={"flex"}
          alignItems={"center"}
          gap={{ base: "20px", md: "28px" }}
        >
          <Box onClick={() => setActiveTab(ANNOUNCE_TYPE.ANNOUNCE)}>
            <Text
              fontSize={{
                base: "20px",
                md: "26px",
              }}
              sx={{
                cursor: "pointer",
                opacity: activeTab === ANNOUNCE_TYPE.ANNOUNCE ? 1 : 0.6,
                transition: "all 0.3s ease-in-out",
                fontWeight: "lighter",
              }}
              _hover={{
                transform: "scale(1.1)",
              }}
            >
              {t("announcement.announcement")}
            </Text>
            <Box
              sx={{
                marginTop: "12px",
                height: "6px",
                backgroundColor:
                  activeTab === ANNOUNCE_TYPE.ANNOUNCE
                    ? "white"
                    : "transparent",
                borderRadius: "6px",
                transition: "background-color 0.3s ease-in-out",
              }}
            ></Box>
          </Box>
          <Box onClick={() => setActiveTab(ANNOUNCE_TYPE.MAINTENANCE)}>
            <Text
              sx={{
                fontSize: {
                  base: "20px",
                  md: "26px",
                },
                opacity: activeTab === ANNOUNCE_TYPE.MAINTENANCE ? 1 : 0.6,
                cursor: "pointer",
                transition: "all 0.3s ease-in-out",
                fontWeight: "lighter",
              }}
              _hover={{
                transform: "scale(1.1)",
              }}
            >
              {t("announcement.maintenance")}
            </Text>
            <Box
              sx={{
                marginTop: "12px",
                height: "6px",
                backgroundColor:
                  activeTab === ANNOUNCE_TYPE.MAINTENANCE
                    ? "white"
                    : "transparent",
                borderRadius: "6px",
                transition: "background-color 0.3s ease-in-out",
              }}
            ></Box>
          </Box>
        </ModalHeader>
        <Box
          sx={{ position: "absolute", width: "100%", zIndex: -1 }}
          top={{ base: "62px", md: "72px" }}
        >
          <Image src={line} alt="line" height={"6px"} />
        </Box>
        <ModalBody
          sx={{
            overflowY: "auto",
            marginBottom: "-24px",
            padding: "16px 16px 0 16px",
          }}
        >
          {activeTab === ANNOUNCE_TYPE.ANNOUNCE ? (
            <AnnouncementContent announcementList={announcementList} />
          ) : (
            <MaintenanceContent />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnnouncementModal;
